import { cn } from '../../../utils/cn';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import type { HeaderNavLinks } from './Header';
import { RoccoLink } from '../../../components/link/RoccoLink';
import { useAtomValue } from 'jotai';
import { currentCategoryAtom } from '../states/current-category-atom';

const _categoriesMenuItemVariants = cva(
    'decoration-3 text-base leading-6 underline-offset-8 data-[active]:underline md:text-5',
    {
        variants: {
            color: {
                light: 'text-white hover:text-white',
                dark: 'text-gray-900 hover:text-gray-900',
            },
        },
        defaultVariants: {
            color: 'light',
        },
    }
);

interface CategoriesMenuProps
    extends VariantProps<typeof _categoriesMenuItemVariants> {
    links: HeaderNavLinks;
    uiUpdatesJune2025Enabled?: boolean;
}

/**
 * The navigation links for the desktop header.
 */
export const CategoriesMenu = (props: CategoriesMenuProps) => {
    const { color, links, uiUpdatesJune2025Enabled } = props;
    const currentCategory = useAtomValue(currentCategoryAtom);

    // When flag is OFF, use text-base for all screen sizes and space-x-20
    // When flag is ON, use the default responsive behavior and space-x-8
    const fontSizeClass = uiUpdatesJune2025Enabled
        ? 'text-base decoration-3 underline-offset-8 data-[active]:underline md:text-5'
        : 'text-base decoration-3 underline-offset-8 data-[active]:underline';

    const spacingClass = uiUpdatesJune2025Enabled ? 'space-x-8' : 'space-x-20';
    const marginTopClass = uiUpdatesJune2025Enabled ? 'mt-6' : 'mt-0';

    return (
        <nav className="w-full">
            <ul className={`flex text-5 ${spacingClass} ${marginTopClass}`}>
                {links.links.map(
                    ({ to, title, prefix, isNonRemixRoute, category }) => {
                        const isCurrent = currentCategory === category;

                        const isHomeDesignEvening = title.includes(
                            'Home Design Evening'
                        );

                        return (
                            <li
                                key={title}
                                style={
                                    isHomeDesignEvening
                                        ? {
                                              marginLeft: 'auto',
                                              paddingRight: '1.5rem',
                                          }
                                        : {}
                                }
                            >
                                <RoccoLink
                                    to={to}
                                    prefetch="intent"
                                    className={cn(
                                        'group block whitespace-nowrap text-5',
                                        fontSizeClass,
                                        color === 'light'
                                            ? 'text-white hover:text-white'
                                            : 'text-gray-900 hover:text-gray-900'
                                    )}
                                    isNonRemixRoute={isNonRemixRoute}
                                >
                                    {prefix}
                                    <br />
                                    <span
                                        className={cn(
                                            'font-medium group-hover:underline group-hover:decoration-[3px]',
                                            isCurrent &&
                                                'underline decoration-[3px]'
                                        )}
                                    >
                                        {title}
                                    </span>
                                </RoccoLink>
                            </li>
                        );
                    }
                )}
            </ul>
        </nav>
    );
};
